import { createSelector } from "reselect"
import { get, find, chain, orderBy, concat, size, omit, toUpper, map, pick, update, includes } from "lodash"
import { ITEMS_COLORS } from "helpers/comparison"
import { dollarFormatter } from "helpers/formatters/numbers"
import { getAllLongShortData } from "modules/compare-segments-root/selectors"
import { getRawTopic, getModels } from "modules/research-topic-root/selectors"
import { getResearchTopicDate } from "modules/research-topic-root/selectors/dates"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as SegmentsCreator } from "modules/compare-segments-root/model"
import { Creators as ResearchTopicCreators } from "modules/research-topic/model"
import { getSegmentNormalization, NORMALIZATION } from "helpers/segments"
import { getRawComparisonItems } from "./model"
import { getRefInstrument, getIsRefInstrumentUnmodeled, getInstrumentDescriptors } from "./instrument"
import { getResearchTopicExperiments } from "."

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

export const getInstrumentPortfolio = state => get(state, "global.data.instrumentPortfolio.data.portfolio", EMPTY_OBJECT)
export const getAccount = state => get(state, "global.selected.account")
export const getHedges = (state) => get(state, "modules.researchTopic.hedges", EMPTY_ARRAY)
export const getInstrumentPortfolioPositions = createSelector(
  [getInstrumentPortfolio],
  (instrumentPortfolio) => get(instrumentPortfolio, "dates.0", {}),
)

export const getInstrumentPortfolioGMV = createSelector(
  [getInstrumentPortfolio],
  (portfolio) => {
    const { gmv, equity } = get(portfolio, "dates.0", {})
    return equity || gmv
  })

export const getBaseItems = createSelector(
  [getRefInstrument, getRawComparisonItems],
  (refInstrument, rawComparisonItems) => {
    if (!refInstrument) {
      return EMPTY_ARRAY
    }
    return [refInstrument, ...rawComparisonItems]
  },
)

const getExperimentItems = createSelector(
  [getBaseItems, getRawTopic, getResearchTopicExperiments, getModels],
  (items, topic, experiments, models) => chain(items)
    .map((baseItem, i) => {
      // Cant chain filter here because we need to keep the original index to later reconstruct the items array
      const experiment = find(experiments, experiment => experiment.id === baseItem.id)
      return experiment && ({
        ...baseItem,
        experiment,
        index: i,
        baseType: baseItem.type,
        type: "inline",
        ...experimentLabel({ experiment, topic, segment: baseItem.segment, models }),
        ...makePositionSet("experiment", { baseItem, experiment, topic }),
      })
    })
    .compact()
    .value(),
)

// We can only add duplicate of the instrument portfolio to comparison items so we only need
// the instrument portfolio positions
const getPortfolioItems = createSelector(
  [getBaseItems, getInstrumentPortfolioPositions, getResearchTopicDate, getInstrumentPortfolio, getAccount, getHedges],
  (items, instrumentPortfolioPositions, researchTopicDate, instrumentPortfolio, account, hedges) => chain(items)
    .map((baseItem, i) => {
      // Cant chain filter here because we need to keep the original index to later reconstruct the items array
      if (baseItem.type !== "portfolio") { return null }

      const hedge = findHedge(baseItem, hedges)
      return {
        ...baseItem,
        index: i,
        baseType: baseItem.type,
        type: "inline",
        hedge,
        portfolio: {
          alias: get(instrumentPortfolio, "alias"),
          link: `/${account}/p/${get(instrumentPortfolio, "alias")}`,
          ...pick(get(instrumentPortfolio, "dates.0"), ["gmv", "equity"]),
        },
        ...portfolioLabel({ ...baseItem, hedge }, instrumentPortfolio),
        ...makePositionSet("portfolio", { baseItem, positions: instrumentPortfolioPositions, researchTopicDate, instrumentPortfolio, hedge }),
      }
    })
    .compact()
    .value(),
)

const getSecurityItems = createSelector(
  [getBaseItems, getInstrumentDescriptors, getResearchTopicDate, getHedges],
  (items, descriptors, researchTopicDate, hedges) => chain(items)
    .map((baseItem, i) => {
      // Cant chain filter here because we need to keep the original index to later reconstruct the items array
      if (baseItem.type !== "security") { return null }

      const hedge = findHedge(baseItem, hedges)
      return {
        ...baseItem,
        index: i,
        baseType: baseItem.type,
        type: "inline",
        descriptors,
        hedge,
        ...securityLabel({ ...baseItem, descriptors, hedge }),
        ...makePositionSet("security", { baseItem, descriptors, researchTopicDate, hedge }),
      }
    })
    .compact()
    .value(),
)

const getItems = createSelector(
  [getPortfolioItems, getSecurityItems, getExperimentItems],
  (pItems, sItems, eItems) => orderBy(concat(pItems, sItems, eItems), "index"),
)

export const getComparisonItems = createSelector(
  [getItems, getAllLongShortData, getIsRefInstrumentUnmodeled],
  (items, longShortData, isRefInstrumentUnmodeled) => map(items, (item, i) => ({
    ...item,
    color: ITEMS_COLORS[i],
    actions: getItemActions(item, size(items), get(item, "experiment"), get(item, "hedge"), longShortData[i], isRefInstrumentUnmodeled),
  })),
)

function getAssetKeyFromClass(assetClass) {
  // swaps and equities are the only supported type for now
  if (assetClass === "swap") {
    return "swaps"
  }
  return "equities"
}

function getInstrumentMeta(instrument) {
  if (!instrument) { return {} }

  if (instrument.assetClass === "swap") {
    return { id: instrument.ticker, key: "swaps" }
  }

  if (instrument.type === "index") {
    return { id: instrument.id, key: "indexes" }
  }

  return {
    id: {
      ...pick(instrument, "ticker", "mic", "modelProviderId", "internalId"),
      mic: instrument.mic || instrument.exchange,
    },
    key: "equities",
  }
}

function findHedge(item, hedges) {
  // If the parent is the refInstrument, the hedge wont have a duplicate id
  const { id, duplicate } = item
  return find(hedges, hedge => (duplicate || id) === hedge.id)
}

function securityPositionSet(item) {
  const { baseItem, descriptors, researchTopicDate, hedge } = item
  const { assetClass, evaluateAs } = baseItem
  const assetClassKey = getAssetKeyFromClass(assetClass)
  const economicExposure = evaluateAs === "short" ? -1 : 1
  const positionSet = {
    dates: [{
      date: researchTopicDate,
      [assetClassKey]: {
        id: pick(descriptors, ["ticker", "mic", "modelProviderId", "internalId"]),
        economicExposure,
      },
    }],
  }
  if (!hedge) { return { positionSet } }

  const { instrument, size } = hedge
  const { id, key } = getInstrumentMeta(instrument)

  const newItem = {
    id,
    economicExposure: economicExposure * (size / 100),
  }

  update(positionSet, ["dates", "0", key], (assets) => concat(assets ?? [], newItem))
  return { positionSet }
}

function securityLabel({ segment, hedge, descriptors, evaluateAs }) {
  // Only security that can be added to the screen is the reference instrument (descriptors)
  const { name, ticker } = descriptors || {}
  if (!hedge) {
    return { label: name || "Instrument" }
  }

  let info = []
  let segmentLabel

  if (segment) {
    // longShort is the only segment supported right now
    segmentLabel = get(segment, "filters.0.longShort")
    info = [
      { label: "Segment", value: segmentLabel },
    ]
  }

  let label = ticker
  if (hedge) {
    const hedgeLabel = getHedgeInstrumentLabel(hedge.instrument)

    if (evaluateAs === "short") {
      label = `${hedgeLabel} hedge - ${label}`
    } else {
      label = `${label} - ${hedgeLabel} hedge`
    }

    info.push({ label: "Hedge Option", value: `${hedgeLabel} @ ${get(hedge, "size")}%` })
  }

  if (segmentLabel) {
    label = `${label} - ${segmentLabel}`
  }

  return {
    label,
    name,
    info: [{
      values: info,
    }],
  }
}

function portfolioPositionSet(item) {
  const { positions, researchTopicDate, instrumentPortfolio, hedge } = item
  const positionSet = {
    dates: [{
      date: researchTopicDate,
      ...omit(positions, "gmv"),
    }],
  }

  if (!hedge) { return { positionSet } }

  const { instrument, size } = hedge
  const { gmv, equity } = get(instrumentPortfolio, "dates.0") || {}
  const { id, key } = getInstrumentMeta(instrument)
  const newItem = {
    economicExposure: (equity || gmv) * (size / 100),
    id,
  }
  update(positionSet, ["dates", "0", key], (assets) => concat(assets ?? [], newItem))
  return { positionSet }
}

function portfolioLabel({ segment, hedge, evaluateAs }, portfolio) {
  let info = []
  let segmentLabel

  if (segment) {
    // longShort is the only segment supported right now
    segmentLabel = get(segment, "filters.0.longShort")
    info = [
      { label: "Segment", value: segmentLabel },
    ]
  }
  const { gmv, equity } = get(portfolio, "dates.0") || {}
  info = [
    ...info,
    { label: "Description", value: portfolio.description },
    { label: equity ? "Reference Equity" : "GMV", value: dollarFormatter(equity || gmv) },
    { label: "Available From", value: portfolio.availableFrom },
  ]

  let label = portfolio.name
  if (hedge) {
    const hedgeLabel = getHedgeInstrumentLabel(hedge.instrument)

    if (evaluateAs === "short") {
      label = `${hedgeLabel} hedge - ${label}`
    } else {
      label = `${label} - ${hedgeLabel} hedge`
    }

    info.push({ label: "Hedge Option", value: `${hedgeLabel} @ ${get(hedge, "size")}%` })
  }

  if (segmentLabel) {
    label = `${label} - ${segmentLabel}`
  }

  return {
    label: label || "Unknown portfolio",
    name: portfolio.name,
    alias: portfolio.alias,
    info: [{
      values: info,
    }],
  }
}

function getHedgeInstrumentLabel(instrument) {
  if (!instrument) { return "" }
  return instrument.ticker || instrument.id
}

function experimentPositionSet({ baseItem, ...infos }) {
  const { id, experiment, topic } = { ...baseItem, ...infos }
  // Smart Trades created from the Research Topic screen won't have a { resourceId, resourceType } as part of their experiment but we know
  // that their resource is the current topic which we are able to get
  const { resourceId, resourceType } = experiment
  const resource = experiment.resourceId ? { id: resourceId, type: resourceType } : topic
  return {
    positionSet: {
      id: resource.id,
      experimentId: id,
      type: toUpper(resource.type || "RESEARCH_TOPIC"),
    },
  }
}

function experimentLabel({ experiment, segment, models }) {
  // Only security that can be added to the screen is the reference instrument (descriptors)
  let info = []
  let segmentLabel
  if (segment) {
    // longShort is the only segment supported right now
    segmentLabel = get(segment, "filters.0.longShort")
    info = [
      { label: "Segment", value: segmentLabel },
    ]
  }
  if (experiment.type === "SMART_TRADE") {
    info = [
      ...info,
      { label: "Description", value: experiment.description },
      { label: "Trade Size", value: dollarFormatter(get(experiment, "config.setup.tradeSize")) },
      {
        label: "Risk Model",
        value: chain(models)
          .find(({ id }) => id === experiment.modelId)
          .get("name")
          .value(),
      },
    ]
  }

  return {
    label: `${experiment.name}${segmentLabel ? ` - ${segmentLabel}` : ""}` || "Unknown experiment",
    name: experiment.name,
    info: [{
      values: info,
    }],
  }
}

function makePositionSet(type, item) {
  switch (type) {
    case "portfolio":
      return portfolioPositionSet(item)
    case "security":
      return securityPositionSet(item)
    case "experiment":
      return experimentPositionSet(item)
  }
  return { positionSet: {} }
}

function getItemActions(item, nbItems, experiment, hedge, longShortData, isRefUnmodeled = false) {
  const isContributor = getSegmentNormalization(item) === NORMALIZATION.CONTRIBUTION
  const experimentType = get(experiment, "type")
  const itemType = get(item, "baseType")
  const smartTradeType = get(experiment, "config.setup.portfolio", false) ? "portfolio" : "trade"
  const actions = [{
    condition: !item.isRefInstrument,
    action: {
      group: 0,
      label: "Remove",
      fn: () => SegmentsCreator.removeComparisonItem(item, "research-topic-remove-comparison"),
    },
  }, {
    condition: !item.isUnmodeled && includes(["security", "swap", "portfolio"], itemType),
    action: {
      group: 0,
      label: `View ${item.name}`,
      link: itemType === "portfolio" && get(item, "portfolio.link"),
      fn: () => itemType === "security" && ProfileCreators.openSecurityProfile2(item.assetClass, item.ticker, item.ticker, item.mic),
    },
  }, {
    condition: !item.segment && includes(["security", "swap"], itemType),
    action: {
      group: 0,
      label: `Evaluate As ${item.evaluateAs === "short" ? "Long" : "Short"}`,
      disabled: item.isUnmodeled,
      fn: () => ResearchTopicCreators.evaluateAs(item.isRefInstrument ? "ref" : item),
    },
  }, {
    condition: !item.segment && itemType === "experiment" && experimentType === "SMART_TRADE",
    action: {
      group: 0,
      label: `View smart ${smartTradeType}`,
      disabled: item.isUnmodeled || isRefUnmodeled,
      fn: () => ResearchTopicCreators.toggleSummaryPanel(item),
    },
  }, {
    condition: item.segment && itemType === "experiment" && experimentType === "SMART_TRADE",
    action: {
      group: 0,
      label: "View Segment",
      fn: () => ResearchTopicCreators.toggleSummaryPanel(item, item.segment),
    },
  }, {
    condition: !item.segment && itemType === "experiment",
    action: {
      group: 1,
      label: "Rename",
      fn: () => ResearchTopicCreators.toggleRenameExperiment(experiment),
    },
  }, {
    condition: !item.segment && itemType === "experiment" && experimentType === "SMART_TRADE",
    action: {
      group: 1,
      label: `Update smart ${smartTradeType}`,
      fn: () => ResearchTopicCreators.toggleUpdateExperiment(experiment),
    },
  }, {
    condition: !item.segment && itemType === "experiment" && experimentType === "PORTFOLIO",
    action: {
      group: 1,
      label: "Upload Positions",
      fn: () => ResearchTopicCreators.toggleUpdateExperiment(item),
    },
  }, {
    condition: !item.segment && itemType === "experiment",
    action: {
      group: 1,
      label: "Delete",
      fn: () => ResearchTopicCreators.deleteExperiment(item),
      confirmationDialog: {
        title: `Delete Experiment: ${item.label}`,
        content: `Are you sure you want to delete ${item.label}? All associated position data will be deleted.`,
        buttonText: "Delete",
      },
    },
  }, {
    condition: !item.segment && (includes(["security", "swap"], itemType) || itemType === "portfolio"),
    action: {
      group: 2,
      label: "Set Hedge Options",
      disabled: item.isUnmodeled,
      fn: () => ResearchTopicCreators.toggleHedgeItem(item),
    },
  }, {
    condition: longShortData && !item.segment,
    action: {
      group: 2,
      label: "Split into LONG/SHORT",
      fn: () => ResearchTopicCreators.splitWithHedge(item, "research-topic-split-item"),
      disabled: nbItems > 3,
      disabledTooltip: "There is not enough space to add the 2 segments",
    },
  }, {
    condition: item.segment,
    action: {
      group: 2,
      label: `Normalize As ${isContributor ? "Standalone" : "Contributor"}`,
      fn: () => SegmentsCreator.setSegmentNormalization(item, isContributor ? NORMALIZATION.STANDALONE : NORMALIZATION.CONTRIBUTION, "research-topic-set-segment-normalization"),
    },
  }, {
    condition: !item.isRefInstrument,
    action: {
      group: 2,
      label: `${item.fxHedgeEnabled ? "Disable" : "Enable"} FX Hedge`,
      fn: () => ResearchTopicCreators.toggleFxHedge(item),
    },
  }]
  return chain(actions)
    .filter(({ condition }) => condition)
    .map(({ action }) => action)
    .value()
}
